<script setup lang="ts">
  import { Motion } from 'motion-v'
  import { Toggle } from '@/components/ui/toggle'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { ScrollArea } from '@/components/ui/scroll-area'
  import router from '@/plugins/router'
  import { computed } from 'vue'
  import { useRoute } from 'vue-router'
  import { useAppStore } from '@/stores/app'
  import { useLocale } from '@/composables/useLocale'
  import { useI18n } from 'vue-i18n'
  import { Icon } from '@iconify/vue'
  import {
    buildFadeTransition,
    buildSidebarItemTransition,
    buildSidebarLabelPreset,
    buildSidebarRevealPreset
  } from '@/utils/motion'
  import { APP_MENU } from '@/navigation'
  import { useAppAbility } from '@/composables/useAppAbility'
  import { useAuthStore } from '@/stores/auth'
  import { storeToRefs } from 'pinia'

  const route = useRoute()
  const { can } = useAppAbility()
  const authStore = useAuthStore()
  const { permissions } = storeToRefs(authStore)

  const allowedByPermission = (perm?: string) => {
    if (!perm) return true
    const i = perm.lastIndexOf('.')
    if (i < 0) return true
    return can(perm.slice(i + 1), perm.slice(0, i))
  }

  const menus = computed(() => {
    // Depend on permissions so the list re-computes on login / hydrate.
    void permissions.value
    return Object.entries(APP_MENU)
      .map(([key, value]) => ({
        key,
        name: value.name,
        routes: value.routes
          .filter((r) => !r.hidden)
          .filter((r) => allowedByPermission(r.permission))
          .map((r) => ({
            ...r,
            active: `/dashboard/${r.path}` === route.path
          }))
      }))
      .filter((menu) => menu.routes.length > 0)
  })

  const handleNavigate = (path: string) => {
    router.replace(`/dashboard/${path}`)
    if (window.innerWidth < 1025) {
      store.toggleSidebar()
    }
  }

  const store = useAppStore()
  const { isRTL } = useLocale()
  const { t } = useI18n()
  const sidebarItemPreset = computed(() => buildSidebarRevealPreset(isRTL.value))
  const sidebarLabelPreset = computed(() => buildSidebarLabelPreset(isRTL.value))
</script>

<template>
  <div
    class="sidebar transition-all duration-300 h-screen bg-bg-primary border-e border-stroke-tertiary fixed top-0 start-0 z-50"
    :style="{
      width: `${store.wrapperWidth}px`
    }"
  >
    <div class="h-full flex flex-col">
      <!-- Toggle Sidebar Button (absolute on border) -->
      <button
        class="sidebar-toggle absolute top-[5%] -translate-y-1/2 -end-4 z-50 size-8 rounded-full bg-card flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-all border border-muted/50 shadow-md"
        @click="store.toggleSidebar()"
      >
        <Icon
          :icon="
            store.sidebarExpanded
              ? isRTL
                ? 'lucide:chevron-right'
                : 'lucide:chevron-left'
              : isRTL
                ? 'lucide:chevron-left'
                : 'lucide:chevron-right'
          "
          class="size-4 transition-transform duration-200"
        />
      </button>

      <!-- Sidebar Header - matches navbar height -->
      <div class="h-[85px] flex items-center justify-center px-4 shrink-0">
        <Motion
          v-if="store.sidebarExpanded"
          :initial="sidebarLabelPreset.initial"
          :animate="sidebarLabelPreset.animate"
          :transition="buildFadeTransition(0.2)"
        >
          <img src="@/assets/images/gudea-base-logo.svg" alt="Gudea Logo" class="max-h-14 mt-4" />
        </Motion>
        <Motion
          v-else
          :initial="sidebarLabelPreset.initial"
          :animate="sidebarLabelPreset.animate"
          :transition="buildFadeTransition(0.2)"
        >
          <img
            src="@/assets/images/gudea-base-logo-small.svg"
            alt="Gudea Logo"
            class="size-14 object-cover"
          />
        </Motion>
      </div>

      <!-- Scrollable Menu Area -->
      <ScrollArea class="flex-1">
        <div class="py-4">
          <div v-for="menu in menus" :key="menu.key" class="mb-4">
            <!-- Section Title -->
            <template v-if="menu.name">
              <Motion
                v-if="store.sidebarExpanded"
                :initial="sidebarLabelPreset.initial"
                :animate="sidebarLabelPreset.animate"
                :transition="buildFadeTransition(0.2)"
                class="px-6 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
              >
                {{ t(menu.name) }}
              </Motion>
              <div v-else class="px-4 py-2">
                <div class="h-px bg-border"></div>
              </div>
            </template>

            <ul>
              <Motion
                as="li"
                v-for="(child, childIndex) in menu.routes"
                :key="`${menu.key}-${child.path}`"
                class="ps-3"
                :initial="sidebarItemPreset.initial"
                :animate="sidebarItemPreset.animate"
                :transition="buildSidebarItemTransition(childIndex)"
              >
                <TooltipProvider disable-hoverable-content>
                  <Tooltip :delay-duration="0">
                    <TooltipTrigger as-child>
                      <Toggle
                        class="sidebar-item !rounded-none relative w-full duration-150 h-11 text-sm flex items-center gap-3 transition-all hover:bg-brand-accent-primary/5"
                        :class="[
                          store.sidebarExpanded ? 'px-4 justify-between' : 'px-0 justify-center',
                          child.active
                            ? 'active-item text-brand-accent-primary'
                            : 'text-shade-secondary hover:text-shade-primary'
                        ]"
                        @click="handleNavigate(child.path)"
                      >
                        <Motion
                          v-if="store.sidebarExpanded"
                          :initial="sidebarLabelPreset.initial"
                          :animate="sidebarLabelPreset.animate"
                          :transition="buildFadeTransition(0.2)"
                          class="truncate"
                          :class="child.active && 'font-medium'"
                        >
                          {{ t(child.title) }}
                        </Motion>
                        <Icon
                          :icon="child.icon"
                          class="size-5 shrink-0 sidebar-icon transition-transform duration-200"
                          :class="
                            child.active ? 'text-brand-accent-primary' : 'text-shade-tertiary'
                          "
                        />
                      </Toggle>
                    </TooltipTrigger>
                    <template v-if="!store.sidebarExpanded">
                      <TooltipContent
                        :side="isRTL ? 'right' : 'left'"
                        class="bg-popover text-popover-foreground border"
                      >
                        <p class="text-sm">{{ t(child.title) }}</p>
                      </TooltipContent>
                    </template>
                  </Tooltip>
                </TooltipProvider>
              </Motion>
            </ul>
          </div>
        </div>
      </ScrollArea>
    </div>
  </div>
</template>

<style scoped>
  /* Toggle button chevron */
  .sidebar-toggle:hover {
    transform: translateY(-50%) scale(1.1);
  }

  .sidebar-toggle:active {
    transform: translateY(-50%) scale(0.95);
  }

  /* Menu item icons bounce on hover */
  .sidebar-item:hover .sidebar-icon {
    transform: scale(1.15);
  }

  /* Gradient border on the end side for active items */
  .sidebar-item.active-item::after {
    content: '';
    position: absolute;
    inset-block: 0;
    inset-inline-end: 0;
    width: 3px;
    background: linear-gradient(
      180deg,
      rgb(var(--gradient-primary-start)) 0%,
      rgb(var(--gradient-primary-end)) 100%
    );
  }
  .active-item {
    background-color: transparent;
  }
</style>
